// bug: sidebar drawer opens inside of an element instead of the body
// cause: drawer has fixed position but if parent has tranform property applied it gets render inside of it, ignores body
// solution: move all drawers inside body element

(() => {
  const PARENT_ID = "sidebar-drawer-parent";

  function ensureParent() {
    let parent = document.getElementById(PARENT_ID);
    if (!parent) {
      parent = document.createElement("div");
      parent.id = PARENT_ID;
      parent.setAttribute("role", "complementary");
      parent.setAttribute("aria-label", "Sidebar drawers");
      document.body.appendChild(parent);
    }
    return parent;
  }

  function moveAllDrawersIntoParent() {
    const parent = ensureParent();

    // Get every <sidebar-drawer> in document order
    const allDrawers = Array.from(document.querySelectorAll("sidebar-drawer"));

    // Filter out ones already inside the parent
    const toMove = allDrawers.filter((el) => el.parentElement !== parent);

    if (toMove.length === 0) return;

    // Minimize reflows
    const frag = document.createDocumentFragment();
    for (const el of toMove) frag.appendChild(el);
    parent.appendChild(frag);
  }

  function observeFutureDrawers() {
    const parent = ensureParent();

    const obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (!(node instanceof Element)) continue;

          // If the added node itself is a drawer…
          if (node.matches?.("sidebar-drawer")) {
            if (node.parentElement !== parent) parent.appendChild(node);
            continue;
          }

          // …or if drawers exist somewhere in the added subtree
          const nested = node.querySelectorAll?.("sidebar-drawer");
          if (nested?.length) {
            const frag = document.createDocumentFragment();
            nested.forEach((d) => {
              if (d.parentElement !== parent) frag.appendChild(d);
            });
            if (frag.childNodes.length) parent.appendChild(frag);
          }
        }
      }
    });

    // Watch the whole document for newly added drawers
    obs.observe(document.documentElement, { childList: true, subtree: true });
  }

  // Run once the DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      moveAllDrawersIntoParent();
      observeFutureDrawers();
    });
  } else {
    moveAllDrawersIntoParent();
    observeFutureDrawers();
  }

  // Optional: expose a manual trigger for debugging
  window.moveSidebarDrawersIntoParent = moveAllDrawersIntoParent;
})();





// -----------------------------------------------------------
// sidebar drawer responsive position

(() => {
  const MOBILE_MQ = window.matchMedia("(max-width: 577px)"); // < 578px matches your CSS
  const INIT_FLAG = "__drawerPosInit";

  function setDrawerPosition(drawer) {
    if (!drawer) return;
    if (drawer.getAttribute("data-auto-position") === "false") return;
    // you can make these data-* if you want per-instance overrides
    const mobilePos = drawer.getAttribute("data-mobile-position") || "bottom";
    const desktopPos = drawer.getAttribute("data-desktop-position") || "right";
    const want = MOBILE_MQ.matches ? mobilePos : desktopPos;

    // Avoid thrashing attributes
    if (drawer.getAttribute("position") !== want) {
      drawer.setAttribute("position", want);
    }
  }

  function initDrawer(drawer) {
    if (!(drawer instanceof Element)) return;
    if (drawer[INIT_FLAG]) return; // already wired
    drawer[INIT_FLAG] = true;

    // initial apply
    setDrawerPosition(drawer);

    // If your <sidebar-drawer> web component internally reflects properties/attributes,
    // this keeps our choice if something else changes it.
    const mo = new MutationObserver(() => setDrawerPosition(drawer));
    mo.observe(drawer, {
      attributes: true,
      attributeFilter: ["data-mobile-position", "data-desktop-position", "data-auto-position"],
    });
    drawer.__posObserver = mo; // optional handle if you later want to disconnect
  }

  function initAll(root = document) {
    root.querySelectorAll("sidebar-drawer").forEach(initDrawer);
  }

  // Apply to current DOM
  document.addEventListener("DOMContentLoaded", () => {
    initAll();

    // Update ALL drawers on breakpoint change (better than resize spam)
    const onMQChange = () => {
      document.querySelectorAll("sidebar-drawer").forEach(setDrawerPosition);
    };
    MOBILE_MQ.addEventListener
      ? MOBILE_MQ.addEventListener("change", onMQChange)
      : MOBILE_MQ.addListener(onMQChange); // older Safari

    // Handle dynamically injected product cards/quick views
    const domObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches?.("sidebar-drawer")) initDrawer(node);
          else initAll(node);
        });
      }
    });
    domObserver.observe(document.body, { childList: true, subtree: true });

    // Theme editor (sections re-render)
    document.addEventListener("shopify:section:load", (e) => initAll(e.target));
  });
})();





// -----------------------------------------------------------
// action: opens quick add drawer

document.addEventListener('click', (e) => {
  const btn = e.target.closest('.quick-add-btn[data-quick-add-drawer-id]');
  if (!btn) return;
  const drawerId = btn.getAttribute('data-quick-add-drawer-id');
  const drawer = document.getElementById(drawerId);
  if (!drawer) return;
  drawer.open();
});





// ----------------------------------------------------------
// action: update quantity on plus/minus button click
const initQuantityControl = (root = document) => {
  root.querySelectorAll("[data-quantity-control]").forEach((wrap) => {
    const input = wrap.querySelector('input[name="quantity"]');
    const minus = wrap.querySelector(".minus");
    const plus = wrap.querySelector(".plus");
    const hasAjaxRequest = wrap.hasAttribute('data-ajax-request');
    const key = wrap.closest('[data-key]')?.dataset.key;

    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const step = Number(input.step) || 1;

    const updateQuantityDisplay = (v) => {
      const val = Math.max(min, Math.min(v, max));
      input.value = String(val);
      minus?.toggleAttribute("disabled", val <= min);
      plus?.toggleAttribute("disabled", Number.isFinite(max) && val >= max);
    };

    const handleQuantityChange = (v) => {
      const val = Math.max(min, Math.min(v, max));
      minus?.toggleAttribute("disabled", val <= min);
      plus?.toggleAttribute("disabled", Number.isFinite(max) && val >= max);
      
      if (hasAjaxRequest) {
        const quantity = Number(val || "1");
        if (!key || !quantity) {
          console.error('Invalid quantity or cart item key');
          return;
        }
        doAjaxRequest(quantity, key);
      } else {
        updateQuantityDisplay(v);
      }
    };


    updateQuantityDisplay(Number(input.value) || min);
    minus?.addEventListener("click", () => handleQuantityChange((Number(input.value) || min) - step));
    plus?.addEventListener("click", () => handleQuantityChange((Number(input.value) || min) + step));



    async function doAjaxRequest(quantity, key) {
      try {
        toggleQuantityLoader();
        const sections = "mini-cart,main-cart";
        
        const response = await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: key, quantity, sections }),
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message);
        }

        const mini_cart_html_string = data.sections['mini-cart'];
        const main_cart_html_string = data.sections['main-cart'];

        if (main_cart_html_string) {
          updateMainCartUI(main_cart_html_string);
        }
        if (mini_cart_html_string) {
          updateMiniCartUI(mini_cart_html_string);
        }

        // update cart count
        window.updateCartItemCount(data.item_count);

      } catch (error) {
        console.log(error);
        window.alert(error.message)
      } finally {
        toggleQuantityLoader();
      }
    }

    function toggleQuantityLoader() {
      const quantityControl = wrap.closest(`[data-quantity-control]`);
      if (!quantityControl) return;
      quantityControl.classList.toggle('loading');
    }

    // Update DOM
    const updateMainCartUI = (htmlString) => {
      const html = document.createElement("div");
      html.innerHTML = htmlString;

      const old_html = document.querySelector('.main-cart-container');
      const new_html = html.querySelector('.main-cart-container');
      if (!old_html) return;

      const updated_quantity = new_html.querySelector(`[data-key="${key}"]`)?.querySelector('input[name="quantity"]')?.value;
      const old_quantity_input = old_html.querySelector(`[data-key="${key}"]`)?.querySelector('input[name="quantity"]');
      if (updated_quantity) {
        old_quantity_input.value = String(updated_quantity);
      }

      // update discount price
      const updated_discount_price = new_html.querySelector("#cart-discount-price");
      const old_discount_price = old_html.querySelector("#cart-discount-price");
      if (updated_discount_price) {
        old_discount_price.innerHTML = updated_discount_price.innerHTML;
      }

      // update subtotal price
      const updated_subtotal_price = new_html.querySelector("#cart-subtotal-price");
      const old_subtotal_price = old_html.querySelector("#cart-subtotal-price");
      if (updated_subtotal_price) {
        old_subtotal_price.innerHTML = updated_subtotal_price.innerHTML;
      }

      // update total price
      const updated_total_price = new_html.querySelector("#cart-total-price");
      const old_total_price  = old_html.querySelector("#cart-total-price");
      if (updated_total_price) {
        old_total_price.innerHTML = updated_total_price.innerHTML;
      }
    }

    const updateMiniCartUI = (htmlString) => {
      const html = document.createElement("div");
      html.innerHTML = htmlString;

      const old_html = document.querySelector("#mini-cart-drawer");
      const new_html = html.querySelector("#mini-cart-drawer");
      if (!old_html) return;

      const updated_quantity = new_html
        .querySelector(`[data-key="${key}"]`)
        ?.querySelector('input[name="quantity"]')?.value;
      const old_quantity_input = old_html
        .querySelector(`[data-key="${key}"]`)
        ?.querySelector('input[name="quantity"]');
      if (updated_quantity) {
        old_quantity_input.value = String(updated_quantity);
      }

      const updated_drawer_footer = new_html.querySelector(".footer-inner");
      const old_drawer_footer = old_html.querySelector(".footer-inner");
      if (updated_drawer_footer) {
        old_drawer_footer.innerHTML = updated_drawer_footer.innerHTML;
      }

      // update upsell UI
      const updateUpsellUI = () => {
        const newUpsell = new_html.querySelector("#cart-upsell");
        const oldUpsell = old_html.querySelector("#cart-upsell");
        if (!newUpsell || !oldUpsell) {
          return;
        }

        const newBar = newUpsell.querySelector(".threshold_bar .animate");
        const oldBar = oldUpsell.querySelector(".threshold_bar .animate");
        const newBarWidth =
          newBar?.style?.getPropertyValue("--bar-width") || null;
        if (newBarWidth && oldBar) {
          oldBar.style.setProperty("--bar-width", newBarWidth);
        }

        // If upsell is complete, replace the entire upsell block
        const upsellComplete = !!newUpsell.querySelector(
          ".threshold_spend.spend_congrats"
        );
        if (upsellComplete) {
          oldUpsell.innerHTML = newUpsell.innerHTML;
          return;
        }

        // Otherwise, just swap the remaining amount/message
        const newSpend = newUpsell.querySelector(
          "#cart-upsell .threshold_spend"
        );
        const oldSpend = oldUpsell.querySelector(
          "#cart-upsell .threshold_spend"
        );
        if (newSpend && oldSpend) {
          oldSpend.innerHTML = newSpend.innerHTML;
        }
      };

      updateUpsellUI();
    }


  });
};

document.addEventListener("DOMContentLoaded", () => {
  initQuantityControl();
});






// ----------------------------------------------------------
// Mini cart: enable/disable checkout based on T&C checkbox
// Use event delegation so it works after dynamic drawer updates
document.addEventListener('change', (e) => {
  const checkbox = e.target.closest('#mini-cart-purchase-conditions-checkbox');
  if (!checkbox) return;
  const checkoutBtn = document.getElementById('mini-cart-checkout-btn');
  if (!checkoutBtn) return;
  checkoutBtn.classList.toggle('disabled', !checkbox.checked);
}, { capture: true });

// ----------------------------------------------------------
// Add to Cart handler
// action: add items to cart
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-add-to-cart]');
  if (!btn) return;

  const scope = btn.dataset.scope;
  const id = btn.dataset.variantId;
  // find the matching qty input anywhere on the page by scope
  const qtyInput = document.querySelector(
    `[data-quantity-control] input[name="quantity"][data-scope="${CSS.escape(scope)}"]`
  );
  const quantity = Math.max(1, parseInt(qtyInput?.value ?? '1', 10) || 1);

  const addToCart = async () => {
    try {

      btn.classList.add('loading');
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: id, quantity, sections: 'mini-cart,header' }),
    })

      const data = await response.json()
      if (!response.ok) {
          throw new Error(data.message | "Couldn't add to cart")
      }

      const htmlString = data.sections['mini-cart'];
      const headerHtmlString = data.sections['header'];
      const html = document.createElement("div")
      html.innerHTML = htmlString;

      const headerHtml = document.createElement("div");
      headerHtml.innerHTML = headerHtmlString;

      const updatedCartCount = headerHtml.querySelector('[data-cart-count]')?.getAttribute('data-cart-count');
      if (updatedCartCount) {
        window.updateCartItemCount(updatedCartCount);
      }
    


      // close all sidebar drawers before opening mini cart
      document.querySelectorAll('sidebar-drawer').forEach(d => d.close());
      
      // replace the mini cart drawer with the new one
      const old_html = document.querySelector('#mini-cart-drawer');
      const new_html = html.querySelector('#mini-cart-drawer');
      if (!old_html || !new_html) return;

      old_html.innerHTML = new_html.innerHTML;
      document.getElementById('mini-cart-drawer').open();

      // re-init quantity control
      initQuantityControl();


    } catch (error) {
      console.error(error);
      
    } finally {
      btn.classList.remove('loading');
    }
  }

  addToCart();
}, { capture: true })





// ----------------------------------------------------------
// Remove item from cart
document.addEventListener('click', (e) => {
  const removeFromCartBtn = e.target.closest('[data-remove-cart-item]');
  if (!removeFromCartBtn) return;

  let cartItemKey = removeFromCartBtn.dataset.cartItemKey
    || removeFromCartBtn.closest('[data-key]')?.dataset.key;

  if (!cartItemKey) {
    console.error("Couldn't find cart item key");
    return;
  }

  const removeFromCart = async () => {
    try {
      removeFromCartBtn.classList.add('loading');

      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: cartItemKey,
          quantity: 0,
          sections: 'mini-cart,main-cart,header'
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Couldn't remove from cart");
      }

      // --- mini cart ---
      if (data.sections['mini-cart']) {
        const wrap = document.createElement('div');
        wrap.innerHTML = data.sections['mini-cart'];

        // update header cart count
        const headerHtml = document.createElement("div");
        headerHtml.innerHTML = data.sections['header'];
        const updatedCartCount = headerHtml.querySelector('[data-cart-count]')?.getAttribute('data-cart-count');

        if (updatedCartCount) {
          window.updateCartItemCount(updatedCartCount);
        }

        const drawer = document.getElementById('mini-cart-drawer');
        const newDrawer = wrap.querySelector('#mini-cart-drawer');

        if (drawer && newDrawer) {
          const wasOpen =
            drawer.hasAttribute('open') || drawer.getAttribute('aria-hidden') === 'false' || drawer.opened;

          drawer.innerHTML = newDrawer.innerHTML;

          if (wasOpen && typeof drawer.open === 'function') drawer.open();

          initQuantityControl(drawer);
        }
      }

      // --- main cart ---
      if (data.sections['main-cart']) {
        const wrap = document.createElement('div');
        wrap.innerHTML = data.sections['main-cart'];

        const currentMain = document.querySelector('.main-cart-container');
        const newMain = wrap.querySelector('.main-cart-container');

        if (currentMain && newMain) {
          currentMain.replaceWith(newMain);
          initQuantityControl(newMain);
        }
      }

      // (header section update if needed)
      // if (data.sections['header']) { ... }

    } catch (err) {
      console.error(err);
      window.alert(err?.message || 'Something went wrong removing the item.');
    } finally {
      removeFromCartBtn.classList.remove('loading');
    }
  };

  removeFromCart();
}, { capture: true });




// ----------------------------------------------------------
// action: play video on hover
const videos = document.querySelectorAll(".hover_video_wrapper video");
if (videos) {
  videos.forEach((video) => {
    video.addEventListener("mouseenter", () => {
      video.play();
    });

    video.addEventListener("mouseleave", () => {
      video.pause();
    });
  });
}